<script setup>
import {ref, computed, onMounted, nextTick, reactive} from 'vue';
import swal from 'sweetalert2';
import UserService from '@/services/user.service';
import moment from 'moment';
import Modal from "@/components/Modal";
import DropzoneFileUpload from "@/components/Inputs/DropzoneFileUpload";
import {useStore} from 'vuex';
import {
    ElInput,
    ElSelect,
    ElOption,
    ElTree,
} from "element-plus";
import VuePdfEmbed from 'vue-pdf-embed'
import "vue3-treeview/dist/style.css";
import YouTube from 'vue3-youtube'

const store = useStore();
const currentUser = computed(() => store.state.auth.user);

const dropzoneInstance = ref({});
const dropzoneFiles = ref([]);
const dropzoneOptions = ref({
    dictDefaultMessage: "Legen Sie Dateien hier ab um Sie hochzuladen",
    dictFallbackMessage: "Ihr Browser unterstützt Drag&Drop Dateiuploads nicht",
    dictFallbackText: "Benutzen Sie das Formular um Ihre Dateien hochzuladen",
    dictFileTooBig: "Die Datei ist zu groß. Die maximale Dateigrösse beträgt {{maxFileSize}}MB",
    dictInvalidFileType: "Eine Datei dieses Typs kann nicht hochgeladen werden",
    dictResponseError: "Der Server hat ihre Anfrage mit Status {{statusCode}} abgelehnt",
    dictCancelUpload: "Hochladen abbrechen",
    dictCancelUploadConfirmation: "Möchten Sie diesen Upload wirklich abbrechen?",
    dictRemoveFile: "Datei entfernen",
    dictMaxFilesExceeded: "Sie können keine weiteren Dateien mehr hochladen",
    autoProcessQueue: false,
    paramName: "file",
});

const validURL = (str) => {
    var pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
    return !!pattern.test(str);
};

const videoLinks = ref([]);
const videoLink = ref('');
const errorVideoLink = ref('');

const deleteVideoLink = (index) => {
    videoLinks.value.splice(index, 1);
};

const addVideoLink = () => {
    if (videoLink.value === "") {
        errorVideoLink.value = '';
    } else {
        if (validURL(videoLink.value)) {
            videoLinks.value.push(videoLink.value);
            videoLink.value = '';
            errorVideoLink.value = '';
        } else {
            errorVideoLink.value = 'Ungültiger Link';
        }
    }
};

const isYoutube = (linkText) => {
    return linkText || (linkText.search(/youtu.be/i) !== -1 || linkText.search(/youtube/) !== -1);
};

const extensionOptions = ref({
    txt: {
        icon: "fad fa-file-lines",
        background: "bg-dark"
    },
    html: {
        icon: "fad fa-note-sticky",
        background: "bg-warning"
    },
    pdf: {
        icon: "fad fa-file-pdf",
        background: "bg-red"
    },
    docx: {
        icon: "fad fa-file-word",
        background: "bg-blue"
    },
    doc: {
        icon: "fad fa-file-word",
        background: "bg-blue"
    },
    dotx: {
        icon: "fad fa-file-word",
        background: "bg-blue"
    },
    xlsx: {
        icon: "fad fa-file-excel",
        background: "bg-green"
    },
    xls: {
        icon: "fad fa-file-excel",
        background: "bg-green"
    },
    csv: {
        icon: "fad fa-file-csv",
        background: "bg-green"
    },
    png: {
        icon: "fad fa-file-image",
        background: "bg-purple"
    },
    jpg: {
        icon: "fad fa-file-image",
        background: "bg-success"
    },
    jpeg: {
        icon: "fad fa-file-image",
        background: "bg-success"
    },
    youtube: {
        icon: "fa-brands fa-youtube",
        background: "bg-red"
    }
});

//treeData
const filterTree = ref();
const nodes = ref([]);
const nodesDownload = ref([]);
const currentNodeKey = ref("0");
const folders = ref([]);
const createFolders = ref([]);
const updateFolders = ref([]);

const sectionRights = ref([]);
const sectionClients = ref([]);

const buildTreeFromData = (data) => {
    const itemMap = new Map();
    data.forEach(item => {
        itemMap.set(item._id, {...item, label: item.text, children: []});
    });
    const rootNodes = [];
    data.forEach(item => {
        const node = itemMap.get(item._id);
        if (!item.parent) {
            rootNodes.push(node);
        } else {
            const parentNode = itemMap.get(item.parent);
            if (parentNode) {
                parentNode.children.push(node);
            }
        }
    });
    return rootNodes;
}
const getAllDescendantIds = (nodeId, treeData) => {
    const descendantIds = [nodeId];

    function findAndCollectDescendants(nodes) {
        if (!nodes || !Array.isArray(nodes)) return false;

        for (const node of nodes) {
            if (node._id === nodeId) {
                collectDescendants(node);
                return true;
            }

            if (node.children && Array.isArray(node.children)) {
                if (findAndCollectDescendants(node.children)) {
                    return true;
                }
            }
        }
        return false;
    }

    function collectDescendants(node) {
        if (!node.children || !Array.isArray(node.children)) return;

        for (const child of node.children) {
            if (typeof child === 'string') {
                descendantIds.push(child);
            } else if (typeof child === 'object' && child._id) {
                descendantIds.push(child._id);
                collectDescendants(child);
            }
        }
    }

    findAndCollectDescendants(treeData);

    return descendantIds;
}

const defaultExpandedKeys = ref([]);
const addToExpandedNodes = (node) => {
    defaultExpandedKeys.value.push(node._id)
}

const removeFromExpandedNodes = (node) => {
    const index = defaultExpandedKeys.value.indexOf(node._id);
    if (index > -1) {
        defaultExpandedKeys.value.splice(index, 1);
    }
}
const getFolders = () => {
    UserService.getFolders().then(
        response => {
            folders.value = response.data.data;
            nodes.value = buildTreeFromData(response.data.data);
            nodesDownload.value = [...nodes.value];
            nodes.value.unshift({text: "Alle", _id: '0', label: "Alle"});
            nextTick(() => {
                filterTree.value.setCurrentKey(currentNodeKey.value);
            });
        },
        error => {
            getError(error);
        }
    );
};

const showSection = ref(false);
const sectionName = ref('');
const showEditSections = ref(false);

const switchEditStates = () => {
    showEditSections.value = !showEditSections.value;
};

const createSection = () => {
    let idNode = 'newNode' + Date.now();
    nodes.value[idNode] = {
        text: sectionName.value,
        sections: sectionRights.value,
        clients: sectionClients.value,
    };
    UserService.createFolder(sectionName.value, '', [], sectionRights.value, sectionClients.value, []).then(
        () => {
            getFolders();
            sectionName.value = '';
            sectionRights.value = [];
            sectionClients.value = [];
            showSection.value = false;
        },
        error => {
            getError(error);
        }
    );
};

const addSubSection = (data) => {
    UserService.createFolder('Neuer Bereich', data._id, [], data.sections, data.clients, []).then(
        () => {
            addToExpandedNodes(data);
            getFolders();
        },
        error => {
            getError(error);
        }
    );
};

const editLabel = (data) => {
    swal.fire({
        icon: 'question',
        title: `Bereich umbenennen`,
        input: "text",
        inputValue: data.label,
        inputValidator: (value) => {
            if (!value) {
                return "Der Bereich braucht einen Namen!";
            }
        },
        buttonsStyling: false,
        customClass: {
            confirmButton: 'btn btn-success btn-fill',
            cancelButton: 'btn btn-secondary btn-fill'
        },
        showCancelButton: true,
        cancelButtonText: 'Schliessen',
        confirmButtonText: 'Umbenennen',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !swal.isLoading()
    }).then((result) => {
        if (result.isConfirmed) {
            data.text = result.value;
            UserService.updateFolder(data).then(
                () => {
                    swal.fire({
                        icon: 'success',
                        title: 'Bereich wurde umbenannt!',
                        showConfirmButton: false,
                        timer: 1500
                    });
                    getFolders();
                },
                error => {
                    getError(error);
                }
            ).catch(error => {
                swal.showValidationMessage(
                    `Request failed: ${error}`
                )
            })
        }
    });
}

const deleteSection = (id) => {
    swal.fire({
        icon: 'warning',
        title: `Bereich wirklich löschen?`,
        buttonsStyling: false,
        customClass: {
            confirmButton: 'btn btn-danger btn-fill',
            cancelButton: 'btn btn-secondary btn-fill'
        },
        showCancelButton: true,
        cancelButtonText: 'Schliessen',
        confirmButtonText: 'Löschen',
        showLoaderOnConfirm: true,
        preConfirm: () => {
            UserService.deleteFolder(id)
                .then(
                    response => {
                        if (!response) {
                            throw new Error(response.statusText)
                        }
                        getFolders();
                        return response.message
                    },
                    error => {
                        getError(error);
                    }
                ).catch(error => {
                swal.showValidationMessage(
                    `Request failed: ${error}`
                )
            })
        },
        allowOutsideClick: () => !swal.isLoading()
    }).then((result) => {
        if (result.isConfirmed) {
            swal.fire({
                icon: 'success',
                title: 'Bereich wurde gelöscht!',
                showConfirmButton: false,
                timer: 1500
            });
        }
    });
};

const filterCurrentNode = (item) => {
    currentNodeKey.value = item['_id'];
    filterFolder.value = item['_id'];
};

const checkedStateArrayCreate = computed(() => {
    return createFolders.value.getCheckedKeys();
});

const checkedStateArrayUpdate = computed(() => {
    return updateFolders.value.getCheckedKeys();
});

const setCheckStates = (statesArray) => {
    updateFolders.value.setCheckedKeys([...statesArray], false);
};

const clearCheckStates = () => {
    createFolders.value.setCheckedKeys([], false);
    updateFolders.value.setCheckedKeys([], false);
};

const showCategory = ref(false);
const categoryName = ref('');

const createCategory = () => {
    UserService.createDownloadCategory(categoryName.value).then(
        () => {
            getCategories();
            categoryName.value = '';
            showCategory.value = false;
        },
        error => {
            getError(error);
        }
    );
};

const showEditCategory = ref(false);
const categoryNameInput = ref();
const updateCategoryId = ref('');
const updateCategoryName = ref('');

const openEditCategory = (category) => {
    showEditCategory.value = true;
    updateCategoryName.value = category.name;
    updateCategoryId.value = category._id;
    nextTick(() => {
        categoryNameInput.value.select();
    });
};

const updateCategory = () => {
    UserService.updateDownloadCategory(updateCategoryId.value, updateCategoryName.value).then(
        () => {
            getCategories();
            categoryName.value = '';
            showEditCategory.value = false;
        },
        error => {
            getError(error);
        }
    );
};

const showEditCategories = ref(false);

const deleteCategory = (id) => {
    swal.fire({
        icon: 'warning',
        title: `Kategorie wirklich löschen?`,
        buttonsStyling: false,
        customClass: {
            confirmButton: 'btn btn-danger btn-fill',
            cancelButton: 'btn btn-secondary btn-fill'
        },
        showCancelButton: true,
        cancelButtonText: 'Schliessen',
        confirmButtonText: 'Löschen',
        showLoaderOnConfirm: true,
        preConfirm: () => {
            UserService.deleteDownloadCategory(id)
                .then(
                    response => {
                        if (!response) {
                            throw new Error(response.statusText)
                        }
                        getCategories();
                        return response.message
                    },
                    error => {
                        getError(error);
                    }
                ).catch(error => {
                swal.showValidationMessage(
                    `Request failed: ${error}`
                )
            })
        },
        allowOutsideClick: () => !swal.isLoading()
    }).then((result) => {
        if (result.isConfirmed) {
            swal.fire({
                icon: 'success',
                title: 'Kategorie wurde gelöscht!',
                showConfirmButton: false,
                timer: 1500
            });
        }
    })
};

const isArchive = ref(false);
const filter = ref(0);
const filterFolder = ref('0');
const downloads = ref([]);

const categories = ref([]);
const getCategories = () => {
    UserService.getDownloadCategories().then(
        response => {
            categories.value = response.data.data;
        },
        error => {
            getError(error);
        }
    );
};

const categoryFilters = ref([]) // enable category multi-filtering
const addToCategoryFilters = (categoryId) => {
    if (categoryFilters.value.includes(categoryId)) {
        categoryFilters.value = categoryFilters.value.filter(catFilter => catFilter !== categoryId)
    } else {
        categoryFilters.value = [...categoryFilters.value, categoryId]
    }
}

const resetCategoryFilters = () => {
    categoryFilters.value = []
}

const categoryButtonState = (categoryId) => {
    return categoryFilters.value.includes(categoryId) ? 'primary' : 'secondary'
}

const getDownloads = () => {
    UserService.getDownloads().then(
        response => {
            downloads.value = [...response.data.files.data, ...response.data.links.data];
        },
        error => {
            getError(error);
        }
    );
};

const youtubeVideo = ref();
const startVideo = () => {
    youtubeVideo.value.playVideo();
};
const stopVideo = () => {
    youtubeVideo.value.stopVideo();
    nextTick(() => {
        checkHTML();
    })
};

const youtubeLink = ref('');
const showVideo = ref(false);

const downloadLink = ref();
const viewPDF = ref(false);
const pdf = ref('');
const search = ref('');
const getDownload = (entry) => {
    if (entry.extension && entry.extension != 'pdf' && entry.extension != 'html') {
        window.open(UserService.forceDownload(entry._id), '_blank');
    }
    if (entry.extension && entry.extension == 'html') {
        UserService.getDownload(entry._id).then(
            response => {
                response.data.text().then(text => {
                    html.body = text;
                    html.title = entry.displayName;
                    showHTML.value = true;
                    nextTick(() => {
                        const elements = htmlBody.value.getElementsByClassName("btn-html");
                        Array.from(elements).forEach(function (element) {
                            element.addEventListener('click', function () {
                                getYoutubeByURL(this.getAttribute('data-url'))
                            });
                        });
                    })
                });
            }
        );
    }
    if (entry.extension && entry.extension == 'pdf') {
        UserService.getDownload(entry._id).then(
            response => {
                const url = window.URL.createObjectURL(new Blob([response.data], {type: "application/pdf"}));
                pdf.value = url;
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.setAttribute('download', entry.displayName + '.pdf');
                document.body.appendChild(link);
                downloadLink.value = link;
                viewPDF.value = true;
            }
        );
    }
    if (entry.url && isYoutube(entry.url)) {
        youtubeLink.value = entry.url;
        showVideo.value = true;
    }
};

const getYoutubeByURL = (url) => {
    youtubeLink.value = url;
    showVideo.value = true;
}

const downloadPDF = (id) => {
    downloadLink.value.click();
};

const convertDate = (date) => {
    return moment(date).format('DD.MM.YYYY');
};

const convertDateTime = (date) => {
    return moment(date).format();
};

const filteredDownloads = computed(() => {
    let returnEntries = downloads.value.sort((a, b) => {
        if (convertDateTime(a.updated_date) < convertDateTime(b.updated_date)) return 1
        if (convertDateTime(a.updated_date) > convertDateTime(b.updated_date)) return -1
        return 0;
    });
    if (categoryFilters.value.length !== 0) {
        returnEntries = returnEntries.filter(entry => entry.categories.some(cat => categoryFilters.value.includes(cat)))
    }
    if (isArchive.value) {
        returnEntries = returnEntries.filter(entry => entry.isArchive);
    } else {
        returnEntries = returnEntries.filter(entry => !entry.isArchive);
    }
    if (filterFolder.value !== '0') {
        const relevantFolderIds = getAllDescendantIds(filterFolder.value, nodes.value);
        returnEntries = returnEntries.filter(item =>
            item.folders.some(folder => relevantFolderIds.includes(folder))
        );
    }
    if (search.value !== '') {
        returnEntries = returnEntries.filter(item => {
            let props = Object.values(item);
            return props.some(prop => !search.value.toLowerCase() || ((typeof prop === 'string') ? prop.toLowerCase().includes(search.value.toLowerCase()) : prop.toString(10).toLowerCase().includes(search.value.toLowerCase())))
        });
    }
    return returnEntries
});

const disabled = ref(false);
const errorText = ref('');

const getError = (error) => {
    if (error.response && error.response.status === 401) {
        errorText.value = 'you are not logged in!';
    } else if (error.response && error.response.status === 403) {
        errorText.value = 'you are not permitted!';
    } else {
        errorText.value =
            (error.response && error.response.data) ||
            error.message ||
            error.toString();
    }
};

const branches = ref([]);
const sections = ref([]);
const clients = ref([]);
const users = ref([]);


const getSections = () => {
    UserService.getRoles().then(
        response => {
            sections.value = response.data.data.map(entry => {
                let role = {};
                role.id = entry.id;
                role.name = entry.name;
                role.description = entry.description;
                role.status = false;
                return role;
            });
            errorText.value = '';
        },
        error => {
            getError(error);
        }
    );
};

const getSectionName = (name) => {
    if (sections.value.find(c => c.name === name)) {
        return sections.value.find(c => c.name === name).description;
    }
    return '';
};

const getClients = () => {
    UserService.getClients().then(
        response => {
            clients.value = response.data.data.filter(entry => entry.addressType === "Organisation" && entry.client_talus === 1).map(entry => {
                let client = {};
                client.id = entry.id.toString();
                client.name = entry.lastName;
                client.city = entry.city;
                client.roles = {
                    rio_user: !!entry.client_rio,
                    abacus_user: !!entry.client_talus,
                    topdesk_user: !!entry.client_talus
                };
                errorText.value = ''
                return client;
            }).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
        },
        error => {
            getError(error);
        }
    );
};

const getUsers = () => {
    UserService.getUsers().then((result) => {
        users.value = result.data.data.filter(entry => entry.firstName !== "TOK" && entry.lastName !== "Admin");
    })
};

const showHTML = ref(false);
const htmlBody = ref();
const html = reactive({
    title: '',
    body: ''
});
const checkHTML = () => {
    if (showHTML.value) {
        document.body.classList.add('modal-open');
    }
};

const showForm = ref(false);
const newCategories = ref([]);
const newBranches = ref([]);
const newSections = ref([]);
const newClients = ref([]);
const newDescription = ref("");
const newUsers = ref([]);

const clearForm = () => {
    categoryError.value = "";
    newCategories.value = [];
    newBranches.value = [];
    newSections.value = [];
    newClients.value = [];
    newUsers.value = [];
    videoLinks.value = [];
    dropzoneFiles.value = [];
    newDescription.value = "";
    dropzoneInstance.value['dropzone'].removeAllFiles();
    clearCheckStates();
};

const categoryError = ref('');
const createDownloads = () => {
    if (newCategories.value.length === 0) {
        categoryError.value = "Bitte eine Kategorie auswählen";
        return false;
    }
    UserService.createDownloads(dropzoneFiles.value, videoLinks.value, newCategories.value, newSections.value, newClients.value, newBranches.value, checkedStateArrayCreate.value, newDescription.value, newUsers.value).then(
        () => {
            getDownloads();
            clearForm();
            showForm.value = false;
        },
        error => {
            getError(error);
        }
    );
};

const showDownload = ref(false);
const downloadNameInput = ref();
const errorDownloadName = ref('');
const editDownloadId = ref(0);
const editDownloadIsArchive = ref(false);
const editDownloadName = ref('');
const editDownloadLink = ref(false);
const editDownloadSections = ref([]);
const editDownloadCategories = ref([]);
const editDownloadClients = ref([]);
const editDownloadUsers = ref([]);
const editDownloadBranches = ref([]);
const editDownloadDescription = ref("");


const clearEditForm = () => {
    errorDownloadName.value = '';
    editDownloadName.value = '';
    editDownloadId.value = 0;
    editDownloadIsArchive.value = false;
    editDownloadLink.value = false;
    editDownloadSections.value = [];
    editDownloadCategories.value = [];
    editDownloadUsers.value = [];
    editDownloadClients.value = [];
    editDownloadBranches.value = [];
    editDownloadDescription.value = "";
    clearCheckStates();
};

const showEditDownload = (item, isLink = false) => {
    editDownloadName.value = item.displayName;
    editDownloadId.value = item['_id'];
    editDownloadIsArchive.value = item.isArchive;
    editDownloadLink.value = isLink;
    editDownloadSections.value = item.sections;
    editDownloadCategories.value = item.categories;
    editDownloadClients.value = item.clients;
    editDownloadBranches.value = item.branches;
    editDownloadUsers.value = (item.users) ? item.users : [];
    editDownloadDescription.value = item.description;

    clearCheckStates();
    setCheckStates(item.folders);
    showDownload.value = true;
    nextTick(() => {
        downloadNameInput.value.select();
    });
};
const updateDownload = () => {
    if (editDownloadName.value.length > 0) {
        UserService.updateDownload({
            isArchive: editDownloadIsArchive.value,
            displayName: editDownloadName.value,
            _id: editDownloadId.value
        }, editDownloadLink.value, editDownloadCategories.value, editDownloadSections.value, editDownloadClients.value, editDownloadBranches.value, checkedStateArrayUpdate.value, editDownloadDescription.value, editDownloadUsers.value).then(
            () => {
                showDownload.value = false;
                getDownloads();
                clearEditForm();
            },
            error => {
                getError(error);
            }
        );
    } else {
        errorDownloadName.value = 'Bitte einen Namen angeben';
    }
};


const archiveDownload = (entry, isLink = false, isArchive) => {
    swal.fire({
        icon: ((isArchive) ? 'info' : 'primary'),
        title: `Den Download wirklich archivieren?`,
        buttonsStyling: false,
        customClass: {
            confirmButton: 'btn ' + ((isArchive) ? 'btn-info' : 'btn-primary') + ' btn-fill',
            cancelButton: 'btn btn-secondary btn-fill'
        },
        showCancelButton: true,
        cancelButtonText: 'Schliessen',
        confirmButtonText: ((isArchive) ? 'Archivieren' : 'Wiederherstellen'),
        showLoaderOnConfirm: true,
        preConfirm: () => {
            entry.isArchive = isArchive;
            UserService.archiveDownload(entry, isLink).then(
                () => {
                    showDownload.value = false;
                    getDownloads();
                    clearEditForm();
                },
                error => {
                    getError(error);
                }
            );
        },
        allowOutsideClick: () => !swal.isLoading()
    }).then((result) => {
        if (result.isConfirmed) {
            swal.fire({
                icon: 'success',
                title: 'Download wurde ' + ((isArchive) ? 'archiviert' : 'wiederhergestellt') + '!',
                showConfirmButton: false,
                timer: 1500
            });
        }
    })
};

const deleteDownload = (id, isLink = false) => {
    swal.fire({
        icon: 'warning',
        title: `Den Download wirklich löschen?`,
        buttonsStyling: false,
        customClass: {
            confirmButton: 'btn btn-danger btn-fill',
            cancelButton: 'btn btn-secondary btn-fill'
        },
        showCancelButton: true,
        cancelButtonText: 'Schliessen',
        confirmButtonText: 'Löschen',
        showLoaderOnConfirm: true,
        preConfirm: () => {
            UserService.deleteDownload(id, isLink)
                .then(
                    response => {
                        if (!response) {
                            throw new Error(response.statusText)
                        }
                        errorText.value = '';
                        getDownloads();
                        return response.message
                    },
                    error => {
                        getError(error);
                    }
                ).catch(error => {
                swal.showValidationMessage(
                    `Request failed: ${error}`
                )
            })
        },
        allowOutsideClick: () => !swal.isLoading()
    }).then((result) => {
        if (result.isConfirmed) {
            swal.fire({
                icon: 'success',
                title: 'Download wurde gelöscht!',
                showConfirmButton: false,
                timer: 1500
            });
        }
    })
};

onMounted(() => {
    getUsers();
    getCategories();
    if (currentUser.value.roles.indexOf('user_admin') !== -1) {
        getSections();
        getClients();
    }
    getDownloads();
    getFolders();
});

</script>
<template>
    <div>
        <base-header type="gradient-success" class="pb-7">
            <div class="row align-items-center py-4">
                <div class="col-lg-6">
                    <h1 class="d-inline-block mb-0">Downloads</h1>
                </div>
            </div>
            <div class="row" v-if="currentUser.roles.indexOf('user_admin') !== -1">
                <modal v-model:show="showForm" modal-classes="modal-xl">
                    <template v-slot:header>
                        <h4 class="modal-title">Neue Dateien hochladen</h4>
                    </template>
                    <div>
                        <div class="row mb-3">
                            <div class="col">
                                <el-select
                                    id="categories"
                                    v-model="newCategories"
                                    class="btn-block"
                                    default-first-option
                                    no-match-text="Kein Eintrag"
                                    filterable multiple
                                    placeholder="Kategorie auswählen">
                                    <el-option
                                        v-for="(category, index) in categories"
                                        :key="index"
                                        :value="category._id"
                                        :label="category.name">
                                    </el-option>
                                </el-select>
                                <small class="errorText" v-if="categoryError.length > 0">{{ categoryError }}</small>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <el-select
                                    id="sections"
                                    v-model="newSections"
                                    class="btn-block"
                                    default-first-option
                                    no-match-text="Kein Eintrag"
                                    filterable multiple
                                    placeholder="Bereiche auswählen (optional)">
                                    <el-option
                                        v-for="(section, index) in sections"
                                        :key="index"
                                        :value="section.name"
                                        :label="section.description">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <el-select
                                    id="clients"
                                    v-model="newClients"
                                    class="btn-block"
                                    default-first-option
                                    no-match-text="Kein Eintrag"
                                    filterable multiple collapse-tags
                                    collapse-tags-tooltip
                                    placeholder="Kunden auswählen (optional)">
                                    <el-option
                                        v-for="(client, index) in clients"
                                        :value="client.id"
                                        :key="index" :label="client.name + ' - ' + client.city">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <!--                        <div class="row mb-3">
                                                    <div class="col">
                                                        <el-select
                                                            id="category"
                                                            v-model="newBranches"
                                                            class="btn-block"
                                                            no-match-text="Kein Eintrag"
                                                            filterable multiple
                                                            placeholder="Branchen auswählen (optional)">
                                                            <el-option
                                                                v-for="(branch, index) in branches"
                                                                :key="index"
                                                                :value="branch.id"
                                                                :label="branch.name">
                                                            </el-option>
                                                        </el-select>
                                                    </div>
                                                </div>-->
                        <div class="row mb-3">
                            <div class="col">
                                <el-select id="users" v-model="newUsers" class="btn-block" default-first-option
                                           no-match-text="Kein Eintrag" filterable multiple collapse-tags
                                           collapse-tags-tooltip
                                           placeholder="Benutzer auswählen (optional)">
                                    <el-option v-for="(user, index) in users" :value="user.id" :key="index"
                                               :label="user.email">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <input class="form-control" name="description" type="text"
                                       placeholder="Beschreibung (Optional)" v-model="newDescription">
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <input class="form-control" name="videoLink" type="text"
                                       placeholder="Youtube Video Link"
                                       v-model="videoLink" @keyup.enter="addVideoLink" @focusout="addVideoLink">
                                <small class="errorText" v-if="errorVideoLink.length > 0">{{
                                        errorVideoLink
                                    }}</small>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <div class="list-group list-group-flush">
                                    <li v-for="(videoLink, index) in videoLinks"
                                        class="list-group-item px-0">
                                        <div class="row align-items-center">
                                            <div class="col-auto">
                                                <div class="avatar">
                                                    <i :class="{'fa-brands fa-youtube' : isYoutube(videoLink)}"></i>
                                                </div>
                                            </div>
                                            <div class="col ml--3">
                                                <a :href="videoLink" target="_blank"><h4 class="w-75">{{
                                                        videoLink
                                                    }}</h4></a>
                                            </div>
                                            <div class="col-auto">
                                                <button class="btn btn-sm btn-danger"
                                                        title="News Beitrag entfernen"
                                                        @click.stop.prevent="deleteVideoLink(index)">
                                                    <i class="fad fa-trash"></i>
                                                </button>
                                            </div>

                                        </div>
                                    </li>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-xl-12">
                                <div class="form-group">
                                    <dropzone-file-upload ref="dropzoneInstance" :options="dropzoneOptions"
                                                          v-model="dropzoneFiles"
                                                          multiple></dropzone-file-upload>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-xl-12">
                                <el-tree
                                    ref="createFolders"
                                    :data="nodesDownload"
                                    node-key="_id"
                                    show-checkbox
                                    check-on-click-node
                                    :expand-on-click-node="false">
                                </el-tree>
                            </div>
                        </div>
                    </div>
                    <template v-slot:footer>
                        <base-button :disabled="disabled" @click.prevent="clearForm"
                                     title="Eingaben löschen"
                                     type="secondary">
                            Eingaben löschen
                        </base-button>
                        <base-button :disabled="disabled" title="Erstellen" @click.prevent="createDownloads"
                                     type="success">
                            Hochladen
                        </base-button>
                    </template>
                </modal>
                <modal v-model:show="showDownload" modal-classes="modal-xl">
                    <template v-slot:header>
                        <h4 class="modal-title">Download bearbeiten</h4>
                    </template>
                    <div>
                        <div class="row mb-3">
                            <div class="col">
                                <input class="form-control" name="downloadName" type="text"
                                       placeholder="Name" @keyup.enter="updateDownload"
                                       v-model="editDownloadName" ref="downloadNameInput">
                                <small class="errorText" v-if="errorDownloadName.length > 0">{{
                                        errorDownloadName
                                    }}</small>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <input class="form-control" name="downloadDescription" type="text"
                                       placeholder="Beschreibung" v-model="editDownloadDescription"
                                       ref="downloadDescriptionInput">
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col shadow-lg--hover">
                                <el-select
                                    id="categories"
                                    v-model="editDownloadCategories"
                                    class="btn-block"
                                    default-first-option
                                    no-match-text="Kein Eintrag"
                                    filterable multiple
                                    placeholder="Kategorie auswählen">
                                    <el-option
                                        v-for="(category, index) in categories"
                                        :key="index"
                                        :value="category._id"
                                        :label="category.name">
                                    </el-option>
                                </el-select>
                                <small class="errorText" v-if="categoryError.length > 0">{{ categoryError }}</small>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <el-select
                                    id="sections"
                                    v-model="editDownloadSections"
                                    class="btn-block"
                                    default-first-option
                                    no-match-text="Kein Eintrag"
                                    filterable multiple
                                    placeholder="Bereiche auswählen (optional)">
                                    <el-option
                                        v-for="(section, index) in sections"
                                        :key="index"
                                        :value="section.name"
                                        :label="section.description">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <el-select
                                    id="clientsDownload"
                                    v-model="editDownloadClients"
                                    class="btn-block"
                                    default-first-option
                                    no-match-text="Kein Eintrag"
                                    filterable multiple collapse-tags
                                    collapse-tags-tooltip
                                    placeholder="Kunden auswählen (optional)">
                                    <el-option
                                        v-for="(client, index) in clients"
                                        :value="client.id"
                                        :key="index" :label="client.name + ' - ' + client.city">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col">
                                <el-select id="users" v-model="editDownloadUsers" class="btn-block" default-first-option
                                           no-match-text="Kein Eintrag" filterable multiple collapse-tags
                                           collapse-tags-tooltip
                                           placeholder="Benutzer auswählen (optional)">
                                    <el-option v-for="(user, index) in users" :value="user.id" :key="index"
                                               :label="user.email">
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                        <!--                        <div class="row mb-3">
                                                    <div class="col">
                                                        <el-select
                                                            id="category"
                                                            v-model="newBranches"
                                                            class="btn-block"
                                                            no-match-text="Kein Eintrag"
                                                            filterable multiple
                                                            placeholder="Branchen auswählen (optional)">
                                                            <el-option
                                                                v-for="(branch, index) in branches"
                                                                :key="index"
                                                                :value="branch.id"
                                                                :label="branch.name">
                                                            </el-option>
                                                        </el-select>
                                                    </div>
                                                </div>-->
                        <div class="row">
                            <div class="col-xl-12">
                                <el-tree
                                    ref="updateFolders"
                                    :data="nodesDownload"
                                    node-key="_id"
                                    show-checkbox
                                    check-on-click-node
                                    :expand-on-click-node="false">
                                </el-tree>
                            </div>
                        </div>
                    </div>
                    <template v-slot:footer>
                        <base-button :disabled="disabled" @click.prevent="clearEditForm"
                                     title="Eingaben löschen"
                                     type="secondary">
                            Eingaben löschen
                        </base-button>
                        <base-button :disabled="disabled" title="Erstellen" @click.prevent="updateDownload"
                                     type="success">
                            Speichern
                        </base-button>
                    </template>
                </modal>
            </div>
        </base-header>
        <div class="container-fluid mt--7">
            <modal v-model:show="showHTML" modal-classes="modal-xl">
                <template v-slot:header>
                    <h4 class="modal-title">{{ html.title }}</h4>
                </template>
                <div v-html="html.body" ref="htmlBody"></div>
            </modal>
            <div class="row">
                <div class="col-lg-3">
                    <card header-classes="bg-transparent" class="card-pricing border-0 mb-4">
                        <template v-slot:header>
                            <div class="row align-items-center">
                                <div class="col">
                                    <div class="h4 text-uppercase mt-3 mb-3">
                                        Bereiche
                                    </div>
                                </div>
                                <div class="col text-right">
                                    <button class="btn btn-sm btn-success"
                                            v-if="currentUser.roles.indexOf('user_admin') !== -1"
                                            title="Neuen Bereich erstellen" @click.prevent="showSection = true;">
                                        <i class="fad fa-plus"></i>
                                    </button>
                                    <button class="btn btn-sm btn-warning" :class="{ 'active': showEditSections}"
                                            title="Kategorien bearbeiten"
                                            v-if="currentUser.roles.indexOf('user_admin') !== -1"
                                            @click.prevent="switchEditStates">
                                        <i class="fad fa-pencil"></i>
                                    </button>
                                </div>
                                <modal v-model:show="showSection" modal-classes="modal-sm">
                                    <template v-slot:header>
                                        <h4 class="modal-title">Neuen Bereich erstellen</h4>
                                    </template>
                                    <div>
                                        <div class="row">
                                            <div class="col">
                                                <div class="form-group">
                                                    <input @keyup.enter="createSection" class="form-control"
                                                           name="title"
                                                           type="text" placeholder="Name"
                                                           v-model="sectionName">
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row mb-3">
                                            <div class="col">
                                                <el-select
                                                    id="sections"
                                                    v-model="sectionRights"
                                                    class="btn-block"
                                                    default-first-option
                                                    no-match-text="Kein Eintrag"
                                                    filterable multiple
                                                    placeholder="Für welche Bereiche anzeigen (optional)">
                                                    <el-option
                                                        v-for="(section, index) in sections"
                                                        :key="index"
                                                        :value="section.name"
                                                        :label="section.description">
                                                    </el-option>
                                                </el-select>
                                            </div>
                                        </div>
                                        <div class="row mb-3">
                                            <div class="col">
                                                <el-select
                                                    id="clientsSection"
                                                    v-model="sectionClients"
                                                    class="btn-block"
                                                    no-match-text="Kein Eintrag"
                                                    default-first-option
                                                    filterable multiple collapse-tags
                                                    placeholder="Für welche Kunden anzeigen (optional)">
                                                    <el-option
                                                        v-for="(client, index) in clients"
                                                        :value="client.id"
                                                        :key="index" :label="client.name + ' - ' + client.city">
                                                    </el-option>
                                                </el-select>
                                            </div>
                                        </div>
                                        <!--                        <div class="row mb-3">
                                                                    <div class="col">
                                                                        <el-select
                                                                            id="category"
                                                                            v-model="newBranches"
                                                                            class="btn-block"
                                                                            no-match-text="Kein Eintrag"
                                                                            filterable multiple
                                                                            placeholder="Branchen auswählen (optional)">
                                                                            <el-option
                                                                                v-for="(branch, index) in branches"
                                                                                :key="index"
                                                                                :value="branch.id"
                                                                                :label="branch.name">
                                                                            </el-option>
                                                                        </el-select>
                                                                    </div>
                                                                </div>-->
                                    </div>
                                    <template v-slot:footer>
                                        <base-button :disabled="disabled" @click.prevent="sectionName = ''"
                                                     title="Eingabe löschen"
                                                     type="secondary">
                                            Eingabe löschen
                                        </base-button>
                                        <base-button :disabled="disabled" title="Erstellen"
                                                     @click.prevent="createSection"
                                                     type="success">
                                            Erstellen
                                        </base-button>
                                    </template>
                                </modal>
                            </div>
                        </template>
                        <el-tree
                            ref="filterTree"
                            class="filterTree"
                            :data="nodes"
                            node-key="_id"
                            @node-expand="addToExpandedNodes"
                            @node-collapse="removeFromExpandedNodes"
                            @current-change="filterCurrentNode"
                            :default-expanded-keys="defaultExpandedKeys"
                            :current-node-key="currentNodeKey"
                            :expand-on-click-node="!showEditSections">
                            <template #default="{ node, data }">
                                <div class="custom-tree-node" :class="{'selected': data.selected}">
                                    <span>{{ node.label }}</span>
                                    <div>
                                        <button class="btn btn-sm btn-warning"
                                                title="Titel bearbeiten"
                                                v-if="showEditSections && node.label !== 'Alle'"
                                                @click.stop.prevent="editLabel(data)">
                                            <i class="fad fa-pencil"></i>
                                        </button>
                                        <button v-if="showEditSections && node.label !== 'Alle'" type="button"
                                                class="btn btn-sm btn-success"
                                                :class="{'mr-4': (data.children && data.children.length !== 0) || node.label === 'Alle'}"
                                                title="Unterbereich erstellen"
                                                @click.stop.prevent="addSubSection(data)"><i
                                            class="fad fa-square-caret-down"></i></button>
                                        <button
                                            v-if="showEditSections && (!data.children || data.children.length === 0) && node.label !== 'Alle'"
                                            type="button" class="btn btn-sm btn-danger mr-4"
                                            title="Bereich entfernen"
                                            @click.stop.prevent="deleteSection(data._id)"><i
                                            class="fad fa-trash"></i></button>
                                    </div>
                                </div>
                                <div class="chosenFolder">
                                    <i v-if="currentNodeKey === data._id" class="fad fa-circle-arrow-right"/>
                                </div>
                            </template>
                        </el-tree>
                    </card>
                </div>
                <div class="col-lg-9">
                    <div class="card">
                        <div class="card-header">
                            <div class="row align-items-center">
                                <div class="col-8">
                                    <base-button class="mt-1 mb-1" size="lg" @click="resetCategoryFilters"
                                                 :type="(filter === 0) ? 'primary' : 'secondary'">
                                        Alle
                                    </base-button>

                                    <base-button class="mt-1 mb-1" v-for="category in categories"
                                                 @click="addToCategoryFilters(category._id)"
                                                 size="lg"
                                                 :type="categoryButtonState(category._id)">
                                        {{ category.name }}
                                        <badge
                                            v-if="showEditCategories && currentUser.roles.indexOf('user_admin') !== -1"
                                            @click.stop.prevent="openEditCategory(category)"
                                            class="btn badgeButton" type="warning"><i class="fad fa-pencil"></i></badge>
                                        <badge
                                            v-if="showEditCategories && currentUser.roles.indexOf('user_admin') !== -1"
                                            @click.stop.prevent="deleteCategory(category._id)"
                                            class="btn badgeButton" type="danger"><i class="fad fa-trash"></i></badge>
                                    </base-button>
                                    <modal v-model:show="showEditCategory" modal-classes="modal-sm">
                                        <template v-slot:header>
                                            <h4 class="modal-title">Kategorie bearbeiten</h4>
                                        </template>
                                        <div>
                                            <div class="row">
                                                <div class="col">
                                                    <div class="form-group">
                                                        <input @keyup.enter="updateCategory" class="form-control"
                                                               name="title" ref="categoryNameInput"
                                                               type="text" placeholder="Name"
                                                               v-model="updateCategoryName">
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <template v-slot:footer>
                                            <base-button :disabled="disabled" @click.prevent="updateCategoryName = ''"
                                                         title="Eingabe löschen"
                                                         type="secondary">
                                                Eingabe löschen
                                            </base-button>
                                            <base-button :disabled="disabled" title="Speichern"
                                                         @click.prevent="updateCategory"
                                                         type="success">
                                                Speichern
                                            </base-button>
                                        </template>
                                    </modal>
                                    <button class="btn btn-sm btn-success"
                                            v-if="currentUser.roles.indexOf('user_admin') !== -1"
                                            title="Neue Kategorie erstellen" @click.prevent="showCategory = true;">
                                        <i class="fad fa-plus"></i>
                                    </button>
                                    <button class="btn btn-sm btn-warning" :class="{ 'active': showEditCategories}"
                                            title="Kategorien bearbeiten"
                                            v-if="currentUser.roles.indexOf('user_admin') !== -1"
                                            @click.prevent="showEditCategories = !showEditCategories;">
                                        <i class="fad fa-pencil"></i>
                                    </button>
                                    <modal v-model:show="showCategory" modal-classes="modal-sm">
                                        <template v-slot:header>
                                            <h4 class="modal-title">Neue Kategorie erstellen</h4>
                                        </template>
                                        <div>
                                            <div class="row">
                                                <div class="col">
                                                    <div class="form-group">
                                                        <input @keyup.enter="createCategory" class="form-control"
                                                               name="title"
                                                               type="text" placeholder="Name"
                                                               v-model="categoryName">
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <template v-slot:footer>
                                            <base-button :disabled="disabled" @click.prevent="categoryName = ''"
                                                         title="Eingabe löschen"
                                                         type="secondary">
                                                Eingabe löschen
                                            </base-button>
                                            <base-button :disabled="disabled" title="Erstellen"
                                                         @click.prevent="createCategory"
                                                         type="success">
                                                Erstellen
                                            </base-button>
                                        </template>
                                    </modal>
                                </div>
                                <div class="col-1">
                                    <base-button class="mt-1 mb-1" size="lg" @click="isArchive = !isArchive"
                                                 :type="(isArchive) ? 'primary' : 'secondary'">
                                        <i class="fad fa-archive"></i> Archiv
                                    </base-button>
                                </div>
                                <div class="col-3 text-right" v-if="currentUser.roles.indexOf('user_admin') !== -1">
                                    <el-input
                                        type="search"
                                        class=""
                                        clearable
                                        prefix-icon="el-icon-search"
                                        style=""
                                        placeholder="Suche"
                                        v-model="search"
                                        aria-controls="datatables">
                                    </el-input>
                                </div>
                            </div>
                        </div>
                        <div class="card-body">
                            <modal v-model:show="showVideo" modal-classes="modal-xl" @open="startVideo"
                                   @close="stopVideo">
                                <div>
                                    <YouTube
                                        width="100%"
                                        height="614"
                                        ref="youtubeVideo"
                                        :src="youtubeLink"/>
                                </div>
                            </modal>
                            <div class="list-group list-group-flush">
                                <base-button v-if="currentUser.roles.indexOf('user_admin') !== -1"
                                             type="success" size="xl mt-3 mb-3" @click.prevent="showForm = true"
                                             title="Dateien hochladen"><i
                                    class="fad fa-file-circle-plus"></i>Dateien hochladen
                                </base-button>
                                <!------- list of downloads  ------->
                                <a href="#" @click.stop.prevent="getDownload(downloadEntry)"
                                   class="list-group-item list-group-item-action"
                                   v-for="downloadEntry in filteredDownloads"
                                   :key="downloadEntry._id">
                                    <div class="row align-items-center">
                                        <div class="col-auto">
                                            <div class="avatar icon" v-if="downloadEntry.extension"
                                                 :class="extensionOptions[downloadEntry.extension].background">
                                                <i :class="extensionOptions[downloadEntry.extension].icon"></i>
                                            </div>
                                            <div class="avatar icon"
                                                 v-if="downloadEntry.url && isYoutube(downloadEntry.url)"
                                                 :class="extensionOptions.youtube.background">
                                                <i :class="extensionOptions.youtube.icon"></i>
                                            </div>
                                        </div>
                                        <div class="col ml--2">
                                            <h4 class="mb-0">
                                                <span>{{ downloadEntry.displayName }}</span>
                                            </h4>
                                            <!--                                    <small>{{ downloadEntry.size }}</small>-->
                                        </div>
                                        <div class="col">
                                            <badge class="mr-2"
                                                   v-for="(section, index) in downloadEntry['sections']"
                                                   :key="index"
                                                   rounded type="primary">
                                                {{ getSectionName(section) }}
                                            </badge>
                                        </div>
                                        <div class="col-auto" v-if="currentUser.roles.indexOf('user_admin') !== -1">
                                            <button type="button" class="btn btn-sm btn-warning"
                                                    title="Download bearbeiten" v-if="downloadEntry.extension"
                                                    @click.stop.prevent="showEditDownload(downloadEntry)"><i
                                                class="fad fa-pencil"></i></button>
                                            <button type="button" class="btn btn-sm"
                                                    :class="{'btn-info': !downloadEntry.isArchive, 'btn-primary': downloadEntry.isArchive}"
                                                    :title="(downloadEntry.isArchive) ? 'Download wiederherstellen': 'Download archivieren'"
                                                    v-if="downloadEntry.extension"
                                                    @click.stop.prevent="archiveDownload(downloadEntry, false, !downloadEntry.isArchive)">
                                                <i class="fad fa-archive"
                                                   :class="{'fa-inbox-out': downloadEntry.isArchive, 'fa-archive': !downloadEntry.isArchive}"></i>
                                            </button>
                                            <button type="button" class="btn btn-sm btn-danger"
                                                    title="Download entfernen" v-if="downloadEntry.extension"
                                                    @click.stop.prevent="deleteDownload(downloadEntry._id)"><i
                                                class="fad fa-trash"></i></button>
                                            <button type="button" class="btn btn-sm btn-warning"
                                                    title="Download bearbeiten" v-if="downloadEntry.url"
                                                    @click.stop.prevent="showEditDownload(downloadEntry, true)"><i
                                                class="fad fa-pencil"></i></button>
                                            <button type="button" class="btn btn-sm"
                                                    :class="{'btn-info': !downloadEntry.isArchive, 'btn-primary': downloadEntry.isArchive}"
                                                    :title="(downloadEntry.isArchive) ? 'Download wiederherstellen': 'Download archivieren'"
                                                    v-if="downloadEntry.url"
                                                    @click.stop.prevent="archiveDownload(downloadEntry, true, !downloadEntry.isArchive)">
                                                <i class="fad"
                                                   :class="{'fa-inbox-out': downloadEntry.isArchive, 'fa-archive': !downloadEntry.isArchive}"></i>
                                            </button>
                                            <button type="button" class="btn btn-sm btn-danger"
                                                    title="Download entfernen" v-if="downloadEntry.url"
                                                    @click.stop.prevent="deleteDownload(downloadEntry._id, true)"><i
                                                class="fad fa-trash"></i></button>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                        <modal v-model:show="viewPDF"
                               modal-classes="modal-xl modal-dialog-centered modal-dialog-scrollable">
                            <template v-slot:header></template>
                            <div class="card mt--2">
                                <vue-pdf-embed :source="pdf"/>
                            </div>
                            <template v-slot:footer>
                                <base-button @click.prevent="downloadPDF" type="primary">
                                    <span class="fad fa-download mr-1"/> Download
                                </base-button>
                            </template>
                        </modal>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss">

.el-tree {
    color: #212529;
    font-size: 1rem;
    border-radius: 100px;
}

.el-tree-node__content {
    font-family: 'futura-pt', sans-serif;
    font-weight: 500;
    min-height: 35px;
}

.filterTree .el-tree-node.is-current > div.el-tree-node__content {
    background-color: #5e72e4;
    border-radius: 2rem;
}

.filterTree .el-tree-node.is-current > div.el-tree-node__content div.custom-tree-node {
    color: #fff;
}

.filterTree .el-tree-node.is-current > div.el-tree-node__content div.chosenFolder {
    color: #fff;
}

.custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 1rem;
    color: #000;
    padding-right: 8px;
}

.chosenFolder {
    position: absolute;
    right: 0.5rem;
}

.newsTeaser span {
    font-size: 1.4em;
    font-weight: 600;
}

.checkbox-wrapper.checked:after {
    width: 0.5rem;
}

.checkbox-wrapper.indeterminate:after {
    width: 0.5rem;
}

.btn .badge.badgeButton {
    overflow: initial;
    margin-right: 0
}

.errorText {
    color: #f5365c;
}

.newsContent span {
    font-size: 1.2em;
}

.level-enter-active,
.level-leave-active {
    transition: all 0.15s ease;
}

.level-enter-from,
.level-leave-to {
    opacity: 0;
}

.btn-html {
    border-radius: inherit !important;
    font-size: 0.9rem !important;
    min-height: 120px;
}

.no-link {
    border-radius: inherit !important;
    font-size: 0.9rem !important;
    cursor: inherit !important;
    min-height: 120px;
}

.abacus-right {
    position: absolute;
    right: -7px;
    top: 50px;
}

.abacus-down {
    position: absolute;
    left: 50%;
    bottom: -15px;
}
</style>

